import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import YouSection from "@/components/apply/YouSection";
import OpportunitySection from "@/components/apply/OpportunitySection";
import ResumeUploader from "@/components/apply/ResumeUploader";
import TierSection from "@/components/apply/TierSection";
import TierSummary from "@/components/apply/TierSummary";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { TIERS } from "@/data/slate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_RESUME_BYTES = 8 * 1024 * 1024; // 8 MB

const INITIAL_FORM = {
    name: "",
    email: "",
    current_title: "",
    target_role: "",
    target_company: "",
    interview_stage: "hiring_manager",
    selected_tier: "campaign",
    jd_link: "",
    jd_text: "",
    notes: "",
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validateForm(form) {
    if (!form.name.trim()) return "Please tell us your name.";
    if (!form.email.trim()) return "Please share an email address.";
    if (!EMAIL_RE.test(form.email)) return "That email doesn't look quite right.";
    if (!form.current_title.trim()) return "Please share your current title.";
    if (!form.target_role.trim()) return "Please share the role you're targeting.";
    return null;
}

function fileToBase64(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const result = reader.result;
            const base64 = typeof result === "string" ? result.split(",")[1] : "";
            resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

const Apply = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const initialTier = searchParams.get("tier") || "campaign";

    const [form, setForm] = useState(() => ({
        ...INITIAL_FORM,
        selected_tier: TIERS.find((t) => t.id === initialTier) ? initialTier : "campaign",
    }));
    const [resumeFile, setResumeFile] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (TIERS.find((t) => t.id === initialTier)) {
            setForm((prev) => ({ ...prev, selected_tier: initialTier }));
        }
    }, [initialTier]);

    const selectedTier = useMemo(
        () => TIERS.find((t) => t.id === form.selected_tier) || TIERS[2],
        [form.selected_tier]
    );

    const updateField = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const onResumeChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > MAX_RESUME_BYTES) {
            toast.error("Resume file is too large. Please upload a file under 8MB.");
            return;
        }
        setResumeFile(file);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const err = validateForm(form);
        if (err) {
            toast.error(err);
            return;
        }
        setSubmitting(true);
        try {
            let resume_base64 = null;
            let resume_filename = null;
            if (resumeFile) {
                resume_base64 = await fileToBase64(resumeFile);
                resume_filename = resumeFile.name;
            }

            const payload = {
                ...form,
                target_company: form.target_company || null,
                jd_link: form.jd_link || null,
                jd_text: form.jd_text || null,
                notes: form.notes || null,
                resume_filename,
                resume_base64,
            };

            const { data } = await axios.post(`${API}/leads`, payload);
            navigate("/thank-you", {
                state: {
                    name: data.name,
                    tier: data.selected_tier,
                    message: data.next_steps_message,
                },
            });
        } catch (error) {
            const detail =
                error?.response?.data?.detail ||
                "Something went wrong. Please try again, or email support@get-slate.ai.";
            toast.error(typeof detail === "string" ? detail : "Submission failed. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#FAF9F6] text-foreground">
            <Header />

            <main className="border-b border-border">
                <div className="mx-auto max-w-6xl px-6 py-16 md:px-10 md:py-24">
                    <Link
                        to="/"
                        data-testid="apply-back-link"
                        className="slate-link inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-muted-foreground"
                    >
                        <ArrowLeft size={14} strokeWidth={1.5} />
                        Back to Slate
                    </Link>

                    <div className="mt-10 grid gap-16 md:grid-cols-12">
                        <div className="md:col-span-7">
                            <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">
                                Start a Campaign
                            </p>
                            <h1
                                data-testid="apply-headline"
                                className="mt-6 font-serif text-4xl font-medium leading-[1.05] tracking-tighter md:text-5xl"
                            >
                                Tell us about your
                                <span className="italic"> next role.</span>
                            </h1>
                            <p className="mt-5 max-w-xl text-muted-foreground">
                                Takes about 3 minutes. You&apos;ll get a personal email within one
                                business day with a payment link and a short kickoff
                                questionnaire.
                            </p>

                            <form
                                data-testid="apply-form"
                                onSubmit={handleSubmit}
                                className="mt-12 space-y-10"
                            >
                                <YouSection form={form} onChange={updateField} />
                                <OpportunitySection form={form} onChange={updateField} />

                                <section className="space-y-6">
                                    <h2 className="font-serif text-2xl font-medium tracking-tight">Your resume</h2>
                                    <p className="text-sm text-muted-foreground">
                                        Upload your current resume in Word or PDF (under 8MB). Optional, but speeds things up considerably.
                                    </p>
                                    <ResumeUploader
                                        file={resumeFile}
                                        onSelect={onResumeChange}
                                        onClear={() => setResumeFile(null)}
                                    />
                                </section>

                                <TierSection selectedTierId={form.selected_tier} onChange={updateField} />

                                <section className="space-y-3">
                                    <Label htmlFor="notes" className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                                        Anything else we should know? (optional)
                                    </Label>
                                    <Textarea
                                        id="notes"
                                        data-testid="field-notes"
                                        value={form.notes}
                                        onChange={(e) => updateField("notes", e.target.value)}
                                        rows={4}
                                        className="rounded-sm border-border bg-card font-sans"
                                        placeholder="Timeline, panel composition, specific concerns — whatever helps us tailor."
                                    />
                                </section>

                                <button
                                    type="submit"
                                    data-testid="apply-submit"
                                    disabled={submitting}
                                    className="inline-flex items-center justify-center gap-3 rounded-sm border border-primary bg-primary px-8 py-4 text-sm font-medium text-primary-foreground transition-colors hover:bg-foreground disabled:opacity-60"
                                >
                                    {submitting ? (
                                        <>
                                            <Loader2 size={16} className="animate-spin" />
                                            Sending…
                                        </>
                                    ) : (
                                        <>
                                            Submit & schedule kickoff
                                            <ArrowRight size={16} strokeWidth={1.5} />
                                        </>
                                    )}
                                </button>
                            </form>
                        </div>

                        <aside className="md:col-span-5">
                            <TierSummary tier={selectedTier} />
                        </aside>
                    </div>
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default Apply;
